import { UserOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Input, Layout, Menu, PageHeader, Row, Table, Tag } from "antd";
import { useApplicationContext } from "components/Application";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const accountUsersQuery = gql`
  query($pagination: ConnectionPagination, $sorting: AccountUserSorting, $searchQuery: String) {
    accountSession {
      id
      user {
        id
        customers(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
          nodes {
            id
            code
            companyName
            email
            role
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            page
            size
          }
          totalCount
          totalPages
        }
      }
    }
  }
`;

const startImpersonatingAccountUserMutation = gql`
  mutation($input: StartImpersonatingAccountUserInput!) {
    startImpersonatingAccountUser(input: $input) {
      successful
    }
  }
`;

export const List: React.FC<{}> = () => {
  const { session, refetchSession } = useApplicationContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "ID" },
    searchQuery,
  };
  const { loading, data } = useQuery(accountUsersQuery, { variables, fetchPolicy: "cache-and-network" });

  const [execMutation] = useMutation(startImpersonatingAccountUserMutation, {
    errorPolicy: "none",
    onCompleted: (data) => {
      refetchSession();
      navigate("/");
    },
  });

  const handleImpersonateClick = (id: string) => {
    execMutation({ variables: { input: { id } } });
  };

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const columns = [
    {
      title: "Ragione Sociale",
      dataIndex: "companyName",
      key: "users.companyName",
      render: (value: string, resource: any) => <p>{value}</p>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "users.email",
      render: (value: string, resource: any) => (
        <NavLink to={resource.id}>
          {value === session.currentUser.email ? <Tag>ME</Tag> : ""}
          {value}
        </NavLink>
      ),
    },
    {
      width: "80px",
      title: "Actions",
      dataIndex: "id",
      render: (id: string) => (
        <Button onClick={() => handleImpersonateClick(id)}>
          <UserOutlined />
          {session.currentUser.language === "IT" ? "Impersonifica" : "Impersonate"}
        </Button>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
              />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader ghost={true} title="Customers" />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.accountSession.user.customers.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: 0,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
